import React, { MouseEventHandler, useContext, useState } from "react";
//Hooks
import { useQueryClient } from "@tanstack/react-query";
import { useToggleModalWithLocalState } from "@vahak/core-ui/dist/hooks/useToggleModalWithLocalState";

import { useUploadImageService } from "../../../../../../services/useUploadImageService";
import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";
import { usePostDocuments } from "../../../../../../services/documentsByReferenceId/useUploadDocumentsService";

//Components
import Container from "@vahak/core-ui/dist/layout/Container";
import Flex from "@vahak/core-ui/dist/layout/Flex";
import Button from "@vahak/core-ui/dist/components/Button/Button";
import ModalOrBottomSheetWrapper from "@vahak/core-ui/dist/components/ModalOrBottomSheetWrapper";
import FileInput from "@vahak/core-ui/dist/components/FileInput";
import Typography from "@vahak/core-ui/dist/components/Typography/Typography";
import { toast } from "@vahak/core/dist/components/toast/toast";
//Constant
import { AppContext } from "@vahak/core/dist/app-context";
import COLORS from "@vahak/core-ui/dist/constants/colors";
import { QueryNames } from "@vahak/core/dist/_services/hooks/queryNames";

//Types
import { DocumentReferenceType, DocumentType, TicketStatus } from "../../../../../../types/document";
import { ButtonOnClickType } from "@vahak/core-ui/dist/components/Button/Button.types";
import { IRcTicketInfo } from "@vahak/core/dist/custom-types/common-preloads";
import { IEventTrackerProp } from "../../../../../../types/events";
import { LorryScreenEvents } from "@vahak/core/dist/constants/event-names";
import { compressImageTo1MB } from "../../../../../../methods/compressImage";

interface LorryActionBarProps extends IEventTrackerProp {
    lorryId: any;
    rcDetails: Partial<IRcTicketInfo> | undefined;
}

export const getLorryVerificationDocumentUrlFromRcData = (rc: Partial<IRcTicketInfo> = {}) => {
    return rc?.info?.documents?.find(({ name }: { name?: string }) => name === "rcDoc")?.url ?? "";
};

const LorryActionBar = ({ lorryId, rcDetails, trackEvent }: LorryActionBarProps) => {
    const { companyId, customerCompanyId } = useContext(AppContext);
    const isMobileScreen = useMediaQuery({ queryType: "mobile" });
    const queryClient = useQueryClient();

    const [file, setFile] = useState<File>();

    const onChange = async (newFile: File) => {
        if (newFile.size < 1 * 1024 * 1024) {
            setFile(newFile);
        } else {
            if (newFile.type.includes("pdf")) {
                toast.error("Please upload an image");
                return;
            }
            const compressedBlob = await compressImageTo1MB(newFile);
            setFile(compressedBlob as any);
        }
    };

    const { isModalOpen: isRCUploadModalOpen, toggleModal: toggleRCUploadModal } = useToggleModalWithLocalState(false);
    const { mutateAsync: uploadImage } = useUploadImageService();
    const { mutateAsync: uploadDocuments } = usePostDocuments();

    const { status: tktStatus, reason = "", ticket_id = 0 } = rcDetails ?? {};
    const isVerificationPending = !!getLorryVerificationDocumentUrlFromRcData(rcDetails);

    let StatusBody, MobileStatusBody;

    const onSizeError = (err: string) => {
        toast.error(err);
    };

    const onClickUploadBtn: ButtonOnClickType = (e) => {
        trackEvent?.(LorryScreenEvents["document_upload_clicked"], {
            source_screen: "my_lorry"
        });
        toggleRCUploadModal();
    };

    const handleModalToggle = () => {
        setFile(undefined);
        toggleRCUploadModal();
    };

    const disableEventBubbling: MouseEventHandler<HTMLDivElement> = (e) => {
        e?.stopPropagation();
    };

    const onSubmitRCFile: ButtonOnClickType = () => {
        uploadImage(
            {
                file: file,
                operation: "RC"
            },
            {
                onSuccess: (res) => {
                    uploadDocuments(
                        {
                            company_id: Number(customerCompanyId ?? companyId),
                            reference_id: lorryId,
                            ticket_id: ticket_id,
                            reference_type: DocumentReferenceType.LORRY,
                            document_type: DocumentType.RC_VALIDATION,

                            payload: {
                                fields: [],
                                documents: [
                                    {
                                        name: "rcDoc",
                                        url: res?.data?.path ?? ""
                                    }
                                ]
                            }
                        },
                        {
                            onSuccess: (res) => {
                                toast.success(res?.data.message ?? "Document Uploaded!!");
                                handleModalToggle();
                                queryClient.invalidateQueries(QueryNames.userLorries.getLorryInfoById);
                                queryClient.invalidateQueries(QueryNames.lorrySearch);
                                trackEvent?.(LorryScreenEvents["document_uploaded"], {
                                    source_screen: "rc_document_verification",
                                    bs_type: "rc_upload",
                                    document_type: "rc"
                                });
                            },
                            onError: (err) => {
                                toast.error(err?.message ?? "Something Went Wrong!!");
                            }
                        }
                    );
                }
            }
        );
    };

    switch (tktStatus) {
        case TicketStatus.REJECTED: {
            StatusBody = (
                <Flex gap={10} alignItems="center">
                    <Typography color={COLORS.RED} weight="bold">
                        Rejected
                    </Typography>
                    <Typography weight="medium">{reason} </Typography>
                    <Button fillType="outlined" size="s" onClick={onClickUploadBtn}>
                        Re Upload
                    </Button>
                </Flex>
            );

            MobileStatusBody = (
                <Flex
                    justifyContent="space-between"
                    style={{ backgroundColor: COLORS.ORANGE_100 }}
                    padding={"8px 16px"}
                    alignItems="center"
                >
                    <Typography color={COLORS.RED} weight="bold" size="sm">
                        Rejected
                    </Typography>
                    <Typography weight="medium" size="sm">
                        {reason}
                    </Typography>
                    <Button fillType="text" onClick={onClickUploadBtn}>
                        <Typography color={COLORS.BLUE} weight="bold" size="s">
                            Re-Upload
                        </Typography>
                    </Button>
                </Flex>
            );
            break;
        }
        default: {
            StatusBody = isVerificationPending ? (
                <Flex gap={10} alignItems="center">
                    <Typography color={COLORS.ORANGE_500} weight="bold">
                        Pending
                    </Typography>
                    <Typography weight="medium">Your RC bill will be verified in 3-4 days </Typography>
                </Flex>
            ) : (
                <Flex gap={10} alignItems="center">
                    <Typography weight="medium" color={COLORS.RED}>
                        Please upload RC to verify your vehicle
                    </Typography>
                    <Button fillType="outlined" size="s" onClick={onClickUploadBtn}>
                        Upload RC Document
                    </Button>
                </Flex>
            );

            MobileStatusBody = isVerificationPending ? (
                <Flex
                    justifyContent="space-between"
                    style={{ backgroundColor: COLORS.ORANGE_100 }}
                    padding={"8px 16px"}
                    alignItems="center"
                >
                    <Typography color={COLORS.ORANGE_500} weight="bold" size="sm">
                        Pending
                    </Typography>
                    <Typography weight="medium" size="s">
                        Your RC bill will be verified in 3-4 days
                    </Typography>
                </Flex>
            ) : (
                <Flex justifyContent="center" style={{ backgroundColor: COLORS.BLUE_100 }} padding={"8px 16px"}>
                    <Button fillType="text" onClick={onClickUploadBtn}>
                        <Typography color={COLORS.BLUE} weight="bold" size="sm">
                            Upload RC Document
                        </Typography>
                    </Button>
                </Flex>
            );
            break;
        }
    }

    return (
        <div>
            {isMobileScreen ? (
                <span onClick={disableEventBubbling}>{MobileStatusBody}</span>
            ) : (
                <Flex justifyContent="flex-end" style={{ marginTop: 20 }}>
                    <Container onClick={disableEventBubbling} style={{ cursor: "default" }}>
                        {StatusBody}
                    </Container>
                </Flex>
            )}
            <span onClick={disableEventBubbling}>
                <ModalOrBottomSheetWrapper
                    isOpen={isRCUploadModalOpen}
                    titleText={"RC document Verification"}
                    onToggle={handleModalToggle}
                    modalSize="wide"
                    sheetBodyHeight="none"
                    sheetBodyMinHeight="none"
                    sheetPaddingBottom={20}
                >
                    <Flex flexDirection="column" gap={20}>
                        <FileInput
                            onChange={onChange}
                            selectedFile={file}
                            ctaText="Browse"
                            onSizeError={onSizeError}
                            fileTypes={["JPG", "PNG", "JPEG", "PDF"]}
                        />
                        <Flex justifyContent="flex-end">
                            <Button disabled={!file} onClick={onSubmitRCFile}>
                                Submit
                            </Button>
                        </Flex>
                    </Flex>
                </ModalOrBottomSheetWrapper>
            </span>
        </div>
    );
};

export default LorryActionBar;
